// @flow
/* eslint-disable max-len */
import type { Map } from "immutable"

// keep lowercase for backward compatibility usage
export const PAYMENT_ERROR_TYPES = {
  // USER ERRORS
  // ===
  // We receive usually useful info such as
  // CARD_EXPIRED, CARD_CVV_INCORRECT, CARD_EXCEEDED_LIMITATION
  USER: "user",

  // SYSTEM ZOOZ ERRORS
  // ===
  // Nonsense errors such as
  // "Call voice authorization number.", "Terminal ID unknown"
  SYSTEM: "system",

  // XHR ERRORS
  // Zooz / BE Api down
  XHR: "xhr",

  // NOT HONOURED ERRORS
  // Previously we tried to fix these errors via filling more details about the payment
  // Still used (for example in src/booking/services/reservation/actions/handleBookingPaymentError.js)
  // do not comment out until it is removed from whole codebase!
  NOT_HONOURED: "not_honoured", // Additional billing requirements

  // UNKNOWN ERRORS
  // Just fallback not happening too often
  // ===
  UNKNOWN: "unknown",
}

export const PAYMENT_ERROR_KEY = {
  BANK_REFUSED: __("common.payment_error.bank_refused"),
  CARD_CVV_INCORRECT: __("common.payment_error.card_cvv_incorrect"),
  CARD_EXCEEDED_LIMITATION: __("common.payment_error.card_exceeded_limitation"),
  CARD_EXPIRED: __("common.payment_error.card_expired"),
  CARD_FUNDS: __("common.payment_error.card_funds"),
  CARD_INVALID: __("common.payment_error.card_invalid"),
  CARD_REPORTED: __("common.payment_error.card_reported"),
  CARD_SECURITY: __("common.payment_error.card_security"),
  FRAUD: __("common.payment_error.fraud"),
}

// https://developers.paymentsos.com/docs/api/#/introduction/responses/response-categories
export const paymentPosErrorResponses = {
  api_authentication_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  api_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  api_request_eSrror: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_authentication_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_network_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  provider_request_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  payment_method_error: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },
  payment_method_declined: {
    type: PAYMENT_ERROR_TYPES.SYSTEM,
  },

  // Only these subcategories are useful for customer
  invalid_card_number: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID,
  },
  invalid_card_cvv: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_CVV_INCORRECT,
  },
  expired_card: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_EXPIRED,
  },
  declined_by_issuing_bank: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID, // TODO: Prepare new translation - add also to Zooz
  },
  declined_by_acquiring_bank: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.CARD_INVALID, // TODO: Prepare new translation
  },
  declined_on_fraud_check: {
    type: PAYMENT_ERROR_TYPES.USER,
    code: PAYMENT_ERROR_KEY.FRAUD,
  },
}

export type CuckooEventDestinations = {
  logmole?:
    | boolean
    | {
        name: string,
      },
  exponea?:
    | boolean
    | {
        name: string,
      },
  ga?: boolean | { category: string, action: string },
  bigQuery?: boolean,
}

export type CuckooEvent = {
  // name: string,
  category:
    | "Booking"
    | "General"
    | "Auth"
    | "Refund"
    | "Watchdog"
    | "Search"
    | "Payment"
    | "Manage"
    | "Manage my booking"
    | "Content"
    | "Cookies"
    | "Account",

  subCategory?: string,
  action: string,
  detail?: string,
  destinations?: ?CuckooEventDestinations,
  legacyName?: string, // name which overrides creating name logic for use in logmole and main exponea
  limit?: "once" | number,
  _last?: any, // internal side effect for checking how many times it was called.
}

const cuckooEventsUnchecked = {
  AUTH_FORGOT_REQUEST: {
    category: "Auth",
    subCategory: "Forgot password",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_FORGOT_ERROR: {
    category: "Auth",
    subCategory: "Forgot password",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_FORGOT_SUCCESS: {
    category: "Auth",
    subCategory: "Forgot password",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_RESET_SUCCESS: {
    category: "Auth",
    subCategory: "Reset password",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_RESET_ERROR_INVALID_NEW_PASSWORD: {
    category: "Auth",
    subCategory: "Reset password",
    action: "Invalid new password",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_RESET_ERROR_INVALID_TOKEN: {
    category: "Auth",
    subCategory: "Reset password",
    action: "Invalid token",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_RESET_ERROR_UNKNOWN: {
    category: "Auth",
    subCategory: "Reset password",
    action: "Unknown error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_SIGNUP_REQUEST: {
    category: "Auth",
    subCategory: "Signup",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_VERIFY_REQUEST: {
    category: "Auth",
    subCategory: "Verify user",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_VERIFY_SUCCESS: {
    category: "Auth",
    subCategory: "Verify user",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_PASSWORD_VERIFY_ERROR: {
    category: "Auth",
    subCategory: "Verify user",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_PASSWORD_REQUEST: {
    category: "Auth",
    subCategory: "Login with password",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_TOKEN: {
    category: "Auth",
    subCategory: "Login with booking id",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_TOKEN_ERROR: {
    category: "Auth",
    subCategory: "Login with booking id",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_TOKEN_SUCCESS: {
    category: "Auth",
    subCategory: "Login with booking id",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_BOOKING_GETTING_IATA_ERROR: {
    category: "Auth",
    subCategory: "Login with booking IATA places data loading",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_ERROR_RETRIEVING_USER: {
    category: "Auth",
    subCategory: "Login",
    action: "Retrieving user info error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_OAUTH_REDIRECT_ERROR: {
    category: "Auth",
    subCategory: "Login",
    action: "Oauth redirect error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGIN_WITH_AUTH_TOKEN: {
    category: "Auth",
    subCategory: "Login",
    action: "Url auth token",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  AUTH_LOGOUT: {
    category: "Auth",
    action: "Logout",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ASSERT_ERROR: {
    category: "Booking",
    action: "assert error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_BAD_REQUEST: {
    category: "Booking",
    action: "Bad request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_LOADING: {
    category: "Booking",
    action: "Booking loading",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true, datadog: true },
  },
  BOOKING_LOADING_ERROR: {
    category: "Booking",
    action: "Booking loading error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_LOADING_SUCCESS: {
    category: "Booking",
    action: "Booking loading success",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_LOADING_SUCCESS_HIDDEN_FLIGHT: {
    category: "Booking",
    subCategory: "Booking loading success",
    action: "Hidden Flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_LOADING_WITHOUT_TOKEN: {
    category: "Booking",
    action: "Booking loading without token-  using flightsId - deprecated",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_LOADING_WITHOUT_TOKEN_ERROR: {
    category: "Booking",
    subCategory: "Booking loading without token",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_LOADING_TRACKING_ERROR: {
    category: "Booking",
    subCategory: "Booking loading",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_LOADING_WITHOUT_LOCATIONS: {
    category: "Booking",
    action: "Booking loading without locations api",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_STARTED_NEW: {
    category: "Booking",
    action: "Booking started new",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_SUBMIT_NEW: {
    category: "Booking",
    action: "Booking submit new",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_SUCCESS: {
    category: "Booking",
    action: "Booking success",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_UPDATE_ERROR: {
    category: "Booking",
    subCategory: "Booking update",
    action: "checkflights failed during update",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_UPDATE_ITINERARY_CHANGE: {
    category: "Booking",
    subCategory: "Booking update",
    action: "itinerary has changed after update",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ADVICES_UPDATE_ERROR: {
    category: "Booking",
    subCategory: "Booking update",
    action: "advices api failed during update",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ADVICES_MORE_INFO: {
    category: "Booking",
    subCategory: "Advices",
    action: "More info click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_REFUND_POLICY: {
    category: "Booking",
    subCategory: "Refund",
    action: "Refund policy displayed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_REFUND_LEARN_MORE: {
    category: "Booking",
    subCategory: "Refund",
    action: "Learn more click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_UNFINISHED_RESTORED: {
    category: "Booking",
    action: "Booking form data restored from unfinished booking",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ABANDONMENT_MODAL_SHOWN: {
    category: "Booking",
    subCategory: "Abandonment",
    action: "Modal shown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ABANDONMENT_ALREADY_SHOWN: {
    category: "Booking",
    subCategory: "Abandonment",
    action: "Modal shown already - cancelling",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ABANDONMENT_MODAL_CHAT_CLICKED_BUT_CHAT_NOT_LOADED: {
    category: "Booking",
    subCategory: "Abandonment",
    action: "Chat clicked - but not loaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ABANDONMENT_MODAL_HELP_CLICKED: {
    category: "Booking",
    subCategory: "Abandonment",
    action: "Modal help clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ABANDONMENT_MODAL_CLOSED: {
    category: "Booking",
    subCategory: "Abandonment",
    action: "Modal closed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_REFUND_MODAL_SHOWN: {
    category: "Booking",
    subCategory: "Refund rules",
    action: "Modal shown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SCHEDULE_CHANGE_MODAL_SHOWN: {
    category: "Booking",
    subCategory: "Journey Change",
    action: "Modal shown",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  SCHEDULE_CHANGE_CONFIRM: {
    category: "Booking",
    subCategory: "Journey Change",
    action: "Confirmed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_NO_BAGS_SEARCH: {
    category: "Booking",
    subCategory: "No bags",
    action: "Redirect on search",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_HAND_BAGS_PRIO_BOARDING_LEARN_MORE: {
    category: "Booking",
    subCategory: "Hand bags with prio boarding",
    action: "learn more click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_HAND_BAGS_PRIO_BOARDING_ADD_HAND_BAG: {
    category: "Booking",
    subCategory: "Hand bags with prio boarding",
    action: "add hand bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_HAND_BAGS_PRIO_BOARDING_REMOVE_HAND_BAG: {
    category: "Booking",
    subCategory: "Hand bags with prio boarding",
    action: "remove hand bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_HAND_BAGS_PRIO_BOARDING_NOT_ORDERED_BUT_PAID: {
    category: "Booking",
    subCategory: "Hand bags with prio boarding",
    action: "not ordered but paid",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PRIORITY_BOARDING_MODAL_OPEN: {
    category: "Booking",
    subCategory: "Priority boarding",
    action: "Open modal",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PRIORITY_BOARDING_MODAL_DISMISS: {
    category: "Booking",
    subCategory: "Priority boarding",
    action: "Modal dismiss",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PRIORITY_BOARDING_MODAL_SAVE: {
    category: "Booking",
    subCategory: "Priority boarding",
    action: "Modal saved",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_ERROR_INVALID_FLIGHTS: {
    category: "Booking",
    subCategory: "Error",
    action: "Flights invalid",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ERROR_MAXIMUM_PASSENGERS_REACHED: {
    category: "Booking",
    subCategory: "Error",
    action: "Maximum passenger reached",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_BAGS_PRICING: {
    category: "Booking",
    subCategory: "Price change",
    action: "Bags prices",
    detail: "No change for user",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_ACCEPTABLE_DEVIATION: {
    category: "Booking",
    subCategory: "Price change",
    action: "Acceptable deviation",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_DECREASE: {
    category: "Booking",
    subCategory: "Price change",
    action: "Decrease",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_INCREASE_INITIAL_TRIGGERED: {
    category: "Booking",
    subCategory: "Price change",
    action: "Increase",
    detail: "Initial triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PRICE_CHANGE_INCREASE_UPDATE_TRIGGERED: {
    category: "Booking",
    subCategory: "Price change",
    action: "Increase",
    detail: "Update triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PRICE_CHANGE_INCREASE_INITIAL_TRACKING_FAILED: {
    category: "Booking",
    subCategory: "Track price change initial",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_INCREASE_UPDATE_TRACKING_FAILED: {
    category: "Booking",
    subCategory: "Track price change update",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PRICE_CHANGE_DURING_PAYMENT: {
    category: "Booking",
    subCategory: "Price change",
    action: "During payment",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FLIGHT_INVALID_INITIAL_TRIGGERED: {
    category: "Booking",
    action: "Flight invalid",
    detail: "Initial triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_FLIGHT_INVALID_UPDATE_TRIGGERED: {
    category: "Booking",
    action: "Flight invalid",
    detail: "Update triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_ALTERNATIVES_MODAL_TRIGGERED: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "Modal triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_ALTERNATIVES_ACCEPT_PRICE_CHANGE: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "Accept price change",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_ALTERNATIVES_SELECT_ALTERNATIVE: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "Select alternative",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_ALTERNATIVES_GO_TO_SEARCH: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "Go to search",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_TOKEN_CHANGE: {
    category: "Booking",
    action: "Token change",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_CURRENCY_CHANGE: {
    category: "Booking",
    action: "Currency changed due to credit card type",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_FIRST_INTERACTION: {
    category: "Booking",
    subCategory: "Form",
    action: "first interaction",
    limit: "once",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_FORM_INPUT: {
    category: "Booking",
    action: "Form input",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_FORM_INPUT_CORRECTED: {
    category: "Booking",
    action: "Form input corrected from suggestion",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_FORM_INPUT_MARKED_AS_CORRECT: {
    category: "Booking",
    action: "Form input marked as correct",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_NAME_CORRECTOR_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Name corrector",
    action: "api triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_NAME_CORRECTOR_API_RETURNED_RESULT: {
    category: "Booking",
    subCategory: "Name corrector",
    action: "api returned result",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_NAME_CORRECTOR_API_ERROR: {
    category: "Booking",
    subCategory: "Name corrector",
    action: "api error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PASSENGER_ADD: {
    category: "Booking",
    action: "Add passenger",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_REMOVE: {
    category: "Booking",
    action: "Remove passenger",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_MAX_LIMIT: {
    category: "Booking",
    action: "Maximum of passengers reached",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_CHANGE_HAND_BAGS: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Change hand bags",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_CHANGE_HOLD_BAGS: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Change hold bags",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_HOLD_BAG_ADD: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Add hold bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_HOLD_BAG_REMOVE: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Remove hold bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_HAND_BAG_ADD: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Add paid hand bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_HAND_BAG_REMOVE: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Remove paid hand bag",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PASSENGER_INSURANCE_CHANGE: {
    category: "Booking",
    subCategory: "Passenger",
    action: "Change insurance",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_INPUT_FIRST_PASSENGER: {
    category: "Booking",
    action: "First form input - passenger",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_INPUT_FIRST_PAYMENT: {
    category: "Booking",
    action: "First form input - payment",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_INPUT_FIRST_CONTACT: {
    category: "Booking",
    action: "First form input - contact",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_AUTOFILL_PASSENGER: {
    category: "Booking",
    subCategory: "Autofill",
    action: "Passenger data autofilled from stored data",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_AUTOFILL_RESTORE: {
    category: "Booking",
    subCategory: "Autofill",
    action: "Booking data restored from last session",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FORM_AUTOFILL_USER_EMAIL: {
    category: "Booking",
    subCategory: "Autofill",
    action: "Email autofilled with logged user email",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_BILLING_DETAILS_EXPANDED: {
    category: "Booking",
    subCategory: "Billing details",
    action: "Expanded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_BILLING_DETAILS_COLLAPSED: {
    category: "Booking",
    subCategory: "Billing details",
    action: "Collapsed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_EXPANDED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Expanded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_COLLAPSED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Collapsed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_API_RESPONSE: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Api response",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Api trigger",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_CLEARED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Cleared",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_ACCEPTED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Accepted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_SUBMITTED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_SUBMITTED_WITH_ERRORS: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Submitted with errors",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_ERROR: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_ERROR_EMAIL_REQUIRED: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Error",
    detail: "Email required",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PROMOCODE_ERROR_UNKNOWN: {
    category: "Booking",
    subCategory: "Promocode",
    action: "Error",
    detail: "unknown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SELFTRANSFER: {
    category: "Booking",
    action: "SelfTransfer - Present and enabled",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SELFTRANSFER_TOOLTIP_SHOWN: {
    category: "Booking",
    action: "SelfTransfer - Tooltip shown",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SELFTRANSFER_ARTICLE: {
    category: "Booking",
    action: "SelfTransfer - HelpCenter article shown",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_STEP_CHANGE: {
    category: "Booking",
    action: "Step change",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT: {
    category: "Booking",
    action: "Submit",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true, datadog: true },
  },
  BOOKING_SUBMIT_NO_BAGS_ITINERARY: {
    category: "Booking",
    action: "Submitted No bags itinerary",
    limit: "once",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_JOURNEY_ITINERARY_NEGATIVE_LAYOVER: {
    category: "Booking",
    action: "Negative layover",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_JOURNEY_ITINERARY_SHORT_LAYOVER: {
    category: "Booking",
    action: "Short layover",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_NOT_CHECKED: {
    category: "Booking",
    action: "Submitted but flights are not checked yet",
    limit: 30,
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PAYMENT_VALIDATION_EMPTY: {
    category: "Booking",
    action: "Payment validation triggered with undefined payment fields",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR_LATIN_ONLY: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    detail: "Names are not in latin only",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR_LATIN_ONLY_SUBMIT: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    detail: "Names are not in latin only - Submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR_LATIN_ONLY_RECHECK: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    detail: "Names are not in latin only - Rechecked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR_NO_ADULTS: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    detail: "No adults",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUBMIT_VALIDATION_ERROR_VIEJET_ADULTS_INFANTS: {
    category: "Booking",
    subCategory: "Submit",
    action: "Validation error",
    detail: "Viejet adult-infants requirement",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_BOOKING: {
    category: "Booking",
    subCategory: "Save Booking",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_GRACE_PERIOD_SHOWN: {
    category: "Booking",
    subCategory: "Grace period",
    action: "modal shown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_GRACE_PERIOD_CHOSEN: {
    category: "Booking",
    subCategory: "Grace period",
    action: "Chosen",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_ERROR_CANT_BOOK_ANYMORE: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Can't book anymore",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_ERROR_UNRECOGNIZED_RESPONSE: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Unrecognized response",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_ERROR_PROMOCODE_ALREADY_USED: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Promocode already used",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_ERROR_UNKNOWN: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Unknown",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_SAVE_ERROR_GDS_RESERVATION_FAILED: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "GDS Reservation failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_ERROR_PAYMENT_CANCELLED: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Payment cancelled",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_SHOWN: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - warning show",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_DECIDED_TO_BOOK: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - decided to book",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_DECIDED_TO_GO_BACK: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - decided to go back",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_PASSENGERS_SHOWN: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - Passengers - warning show",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_PASSENGERS_DECIDED_TO_BOOK: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - Passengers - decided to book",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_WARNING_DUPLICATE_PASSENGERS_DECIDED_TO_GO_BACK: {
    category: "Booking",
    subCategory: "Save",
    action: "Error",
    detail: "Duplicate - Passengers - decided to go back",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_PRICES_DIFFER_GRACE_PERIOD_USED: {
    category: "Booking",
    subCategory: "Save",
    action: "Accepted price differs fro charged amount",
    detail: "adding grace period",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_PRICES_DIFFER_CANCELLING: {
    category: "Booking",
    subCategory: "Save",
    action: "Accepted price differs fro charged amount",
    detail: "cancelling showing error and reloading page",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_SAVE_PRICES_DIFFER_CONTINUING: {
    category: "Booking",
    subCategory: "Save",
    action: "Accepted price differs fro charged amount",
    detail: "difference lower than 1 eur continuing",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUMMARY_OPEN_REQUEST: {
    category: "Booking",
    subCategory: "Summary",
    action: "Request to render booking summary",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUMMARY_INVALID_FORM: {
    category: "Booking",
    subCategory: "Summary",
    action: "Booking summary not opened because of invalid form",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SUMMARY_SUBMIT: {
    category: "Booking",
    subCategory: "Summary",
    action: "Submitted from modal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_ACCOUNT_BALANCE_CHANGE: {
    category: "Booking",
    subCategory: "Payment",
    action: "Account balance change",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_NOTHING_TO_PAY: {
    category: "Booking",
    subCategory: "Payment",
    action: "Nothing to pay",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_ERROR_MISSING_TRANSLATION_FOR_ERROR_MESSAGE: {
    category: "Booking",
    subCategory: "Payment",
    action: "Error",
    detail: "missing translation for error message",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_REDIRECT: {
    category: "Booking",
    subCategory: "Payment",
    action: "Redirecting to payment gateway",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_ALTERNATIVE_SUCCESSFUL: {
    category: "Booking",
    subCategory: "Payment",
    action: "Alternative",
    detail: "Successful APM payment",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_ALTERNATIVE_UNSUCCESSFUL: {
    category: "Booking",
    subCategory: "Payment",
    action: "Alternative",
    detail: "Unsuccessful",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_ALTERNATIVE_ERROR_RETRIEVING_INFO: {
    category: "Booking",
    subCategory: "Payment",
    action: "Alternative",
    detail: "Error when retrieving status",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_ALTERNATIVE_ERROR_UNEXPECTED_REDIRECT: {
    category: "Booking",
    subCategory: "Payment",
    action: "Alternative",
    detail: "Unexpected redirect",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_ALTERNATIVE_ERROR_NO_REDIRECT_URL: {
    category: "Booking",
    subCategory: "Payment",
    action: "Alternative",
    detail: "No redirect url",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_SAVE_SUCCESSFUL_METHOD: {
    category: "Booking",
    subCategory: "Payment",
    action: "Save booking successful",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_COMPLETE: {
    category: "Booking",
    action: "Complete",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_COMPLETE_TRACKING_FAILED: {
    category: "Booking",
    subCategory: "Tracking booking complete",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_REDIRECT: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Redirect to bank",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_SUCCESSFUL: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Successful APM payment",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_UNSUCCESSFUL: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Unsuccessful",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_ERROR_RETRIEVING_INFO: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Error when retrieving status",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_ERROR_UNEXPECTED_REDIRECT: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Unexpected redirect",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3D_ERROR_CANCELLED: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "Cancelled",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_INIT_TRIGGERED: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script init triggered",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_FORTER_SCRIPT_NOT_FOUND: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script was not found",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_INIT_ERROR: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script init error",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_INIT_SUCCESS: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script init success",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_CHALLENGE_TRIGGERED: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script challenge triggered",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_CHALLENGE_ERROR: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script challenge error",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_CHALLENGE_FAILED: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script challenge failed showing error",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_3DS_CHALLENGE_SUCCESS: {
    category: "Booking",
    subCategory: "Payment",
    action: "3D Secure",
    detail: "forter script challenge success",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PAYMENT_ERROR_USER: {
    category: "Booking",
    subCategory: "payment error",
    action: "user - showing payment help",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PAYMENT_ERROR_SYSTEM: {
    category: "Booking",
    subCategory: "payment error",
    action: "system - showing general error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PAYMENT_ERROR_XHR: {
    category: "Booking",
    subCategory: "payment error",
    action: "xhr - showing general error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PAYMENT_ERROR_UNKNOWN: {
    category: "Booking",
    subCategory: "payment error",
    action: "unknown - showing general error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_CONFIRMED: {
    category: "Booking",
    action: "Confirmed",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_CONFIRMED_TRACKING_ERROR: {
    category: "Booking",
    subCategory: "Confirmed",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_THANKYOU_DEPRECATED: {
    category: "Booking",
    action: "Depracated Thankyou url visited redirecting to new one",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_FARE_CONDITIONS_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Fare conditions",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_FARE_CONDITIONS_API_ERROR: {
    category: "Booking",
    subCategory: "Fare conditions",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ALTERNATIVES_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ALTERNATIVES_API_ERROR: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ALTERNATIVES_SAME: {
    category: "Booking",
    subCategory: "Alternatives",
    action: "Same booking offered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_SAVE_BOOKING_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Save booking",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_SAVE_BOOKING_API_ERROR: {
    category: "Booking",
    subCategory: "Save booking",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_CONFIRM_PAYMENT_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Confirm payment",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_CONFIRM_PAYMENT_API_ERROR: {
    category: "Booking",
    subCategory: "Confirm payment",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_AMENITIES_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Amenities",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_AMENITIES_API_ERROR: {
    category: "Booking",
    subCategory: "Amenities",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_CHECK_FLIGHTS_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Check flights",
    action: "api triggered",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_CHECK_FLIGHTS_API_SUCCESS: {
    category: "Booking",
    subCategory: "Check flights",
    action: "api success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_CHECK_FLIGHTS_API_SHOULD_CANCEL_PREVIOUS: {
    category: "Booking",
    action: "Should cancel previous fetch",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_CHECK_FLIGHTS_API_ERROR: {
    category: "Booking",
    subCategory: "Check flights",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_CHECK_FLIGHTS_API_SUPPRESSED: {
    category: "Booking",
    subCategory: "Check flights",
    action: "api suppressed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  BOOKING_TRAVEL_TIP_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "api triggered",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: false, datadog: true },
  },
  BOOKING_TRAVEL_TIP_API_RETURNED_RESULT: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "api returned result",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: false },
  },
  BOOKING_TRAVEL_TIP_API_NO_RESULT: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "no result",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true, datadog: true },
  },
  BOOKING_TRAVEL_TIP_API_ERROR: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "error",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true, datadog: true },
  },
  BOOKING_TRAVEL_TIP_MODAL_OPENED: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "modal opened",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_TRAVEL_TIP_MODAL_BOOK_CLICKED: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "book button clicked",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_TRAVEL_TIP_SHOWN: {
    category: "Booking",
    subCategory: "Travel tip",
    action: "shown",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "api triggered",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: false, datadog: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_API_ERROR: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "api error",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: false, datadog: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_API_SUCCESS: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "api success",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: false },
  },
  BOOKING_HOTELS_ADVERTISEMENT_DURATION: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "duration",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_DISPLAY_TIMEOUT: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "display timeout",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true, datadog: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_LESS_RESULTS: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "less results",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_DIRECT_LINK_CLICKED: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "direct link clicked",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_MODAL_OPENED: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "modal opened",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_MODAL_REDIRECT: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "modal redirect",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_HOTELS_ADVERTISEMENT_MODAL_SHOWN: {
    category: "Booking",
    subCategory: "Hotels advertisement",
    action: "shown",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_DURATION_TRACKING_FAILED: {
    category: "Booking",
    action: "Duration tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },

  // ANCILLARIES 3rdBOOKING_ADDITIONAL_SERVICES_API_SUCCESS
  RESERVATION_FORM_VARIANT: {
    category: "Booking",
    subCategory: "Variant",
    action: "Rendered Booking variant",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },
  BOOKING_ADDITIONAL_SERVICE: {
    category: "Booking",
    action: "Booking - Additional service",
  },
  BOOKING_ANCILLARIES_3RD_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Ancillaries 3rd",
    action: "API call started",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ANCILLARIES_3RD_API_ERROR: {
    category: "Booking",
    subCategory: "Ancillaries 3rd",
    action: "Ancillaries 3rd API error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false, datadog: true },
  },
  BOOKING_ANCILLARIES_3RD_API_SUCCESS: {
    category: "Booking",
    subCategory: "Ancillaries 3rd",
    action: "Ancillaries 3rd API success",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },
  BOOKING_ANCILLARIES_3RD_API_DURATION: {
    category: "Booking",
    subCategory: "Ancillaries 3rd",
    action: "duration",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },

  BOOKING_CHECK_ANCILLARIES_API_TRIGGERED: {
    category: "Booking",
    subCategory: "Check Ancillaries",
    action: "API call started",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },
  BOOKING_CHECK_ANCILLARIES_API_ERROR: {
    category: "Booking",
    subCategory: "Check Ancillaries",
    action: "Check Ancillaries API error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },
  BOOKING_CHECK_ANCILLARIES_API_SUCCESS: {
    category: "Booking",
    subCategory: "Check Ancillaries",
    action: "Check Ancillaries API success",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },

  PRE_BOOKING_API_TRIGGERED: {
    category: "Booking",
    action: "PreBooking Api Triggered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false, datadog: true },
  },

  PRE_BOOKING_API_SUCCESS: {
    category: "Booking",
    action: "PreBooking Api Success",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },

  PRE_BOOKING_API_ERROR: {
    category: "Booking",
    action: "PreBooking Api Error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false, datadog: true },
  },

  // GENERAL
  // ===
  BOOKING_EXIT_BANNER_TRIGGERED: {
    category: "Booking",
    subCategory: "Exit banner",
    action: "triggered",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_EXIT_BANNER_ACCEPTED: {
    category: "Booking",
    subCategory: "Exit banner",
    action: "accepted",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_EXIT_BANNER_CLOSED: {
    category: "Booking",
    subCategory: "Exit banner",
    action: "closed",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  BOOKING_PB_SOLD_OUT_MARKED_FLIGHTS: {
    category: "Booking",
    subCategory: "Priority boarding",
    action: "sold-out",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  GENERAL_UNSUPPORTED_CURRENCY: {
    category: "General",
    action: "Unsupported currency",
    limit: 30,
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_LANGUAGE_CHANGE: {
    category: "General",
    action: "Global changing language",
    legacyName: "Global changing language",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_PAGE_LOADED: {
    category: "General",
    action: "Page loaded",
    legacyName: "Page loaded",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  GENERAL_PAGE_LEFT: {
    category: "General",
    action: "User left the page",
    legacyName: "User left the page",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  GENERAL_PAGE_INITIALIZED: {
    category: "General",
    action: "Page initialized",
    legacyName: "Page initialized",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_OPEN_MODAL: {
    category: "General",
    action: "open info modal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_PAGE_PARSING: {
    category: "General",
    action: "Page parsing",
    legacyName: "Page parsing",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_PERFORMANCE: {
    category: "General",
    action: "Performance client",
    legacyName: "Performance client",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_PERFORMANCE_UNAVAILABLE: {
    category: "General",
    action: "Performance unavailable",
    legacyName: "Performance unavailable",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_OPEN_CHAT_CLICKED: {
    category: "General",
    action: "Open chat clicked",
    legacyName: "Open chat clicked",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_RENDER_ERROR: {
    category: "General",
    action: "Render error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_OPEN_ARTICLE: {
    category: "General",
    action: "Open FAQ Article",
    legacyName: "Open FAQ Article",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_ERROR_UTM_LOCAL_STORAGE_LOAD: {
    category: "General",
    action: "UTM local storage load failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_ERROR_UTM_LOCAL_STORAGE_SET: {
    category: "General",
    action: "UTM local storage set failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_CURRENCY_SWITCHER_OPEN: {
    category: "General",
    subCategory: "Currency switcher",
    action: "Open",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_CURRENCY_SWITCHER_CHANGE: {
    category: "General",
    subCategory: "Currency switcher",
    action: "Change currency",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  GENERAL_TRACKING_FAILED: {
    category: "General",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_ADDITIONAL_ACTION_API_ERROR: {
    category: "General",
    action: "Additional action api error",
    legacyName: "Additional action api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  INTERNAL_ADDITIONAL_ACTION_SUCCESS: {
    category: "General",
    action: "Additional action appending html",
    legacyName: "Additional action appending html",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_BAGS_UNSUPPORTED_FORMAT: {
    category: "General",
    subCategory: "Bag params mapper",
    action: "Unsupported format",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_DOCUMENT_NEED_UNSUPPORTED: {
    category: "General",
    subCategory: "Mapper document need",
    action: "unrecognized input",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_AIRLINE: {
    category: "General",
    subCategory: "Flight mapper",
    action: "Unsupported format of airline",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_LOCATION: {
    category: "General",
    subCategory: "Flight mapper",
    action: "Unsupported format of location",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_TIME: {
    category: "General",
    subCategory: "Flight mapper",
    action: "Unsupported format of time",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_JOURNEY_UNSUPPORTED_FORMAT: {
    category: "General",
    subCategory: "Journey mapper",
    action: "Unsupported format",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_FLIGHTS_HISTORY_UNSUPPORTED_FORMAT: {
    category: "General",
    subCategory: "Flights history mapper",
    action: "Unsupported format",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_PENDING_FLIGHT_CHANGE_OPTIONS_UNSUPPORTED_FORMAT: {
    category: "General",
    subCategory: "Pending flight change options mapper",
    action: "Unsupported format",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  INTERNAL_MAPPER_PENDING_REQUESTS_UNSUPPORTED_FORMAT: {
    category: "General",
    subCategory: "Pending requests params mapper",
    action: "Unsupported format",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_ACTION_ERROR: {
    category: "Manage",
    action: "Action failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_FORM_INPUT_CORRECTED: {
    category: "Manage",
    action: "Form input corrected from suggestion",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_INIT: {
    category: "Manage",
    action: "init",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BOOKING_LOADING_ERROR: {
    category: "Manage",
    action: "loading booking error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_LOAD_BOOKINGS_ERROR: {
    category: "Manage",
    action: "loading bookings error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_LOAD_TRANSACTIONS_ERROR: {
    category: "Manage",
    action: "loading transactions error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_LOAD_WATCHDOGS_ERROR: {
    category: "Manage",
    action: "loading watchdogs error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_REQUESTED_BOOKING_NOT_FOUND: {
    category: "Manage",
    action: "booking not found",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_THANKYOU_RENDERED: {
    category: "Manage",
    action: "Thankyou rendered",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_THANKYOU_ALREADY_RENDERED: {
    category: "Manage",
    subCategory: "Thankyou",
    action: "Already rendered before not going to track conversion again",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_SWITCH_TAB: {
    category: "Manage",
    action: "switch tab",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PAYMENT_REUSING_TOKEN: {
    category: "Manage",
    subCategory: "Payment",
    action: "Reusing payment token as it was created less than 15m ago",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_PAY_REQUEST: {
    category: "Manage",
    subCategory: "Payment",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_PAY_ERROR: {
    category: "Manage",
    subCategory: "Payment",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_PAY_SUCCESS: {
    category: "Manage",
    subCategory: "Payment",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PAYMENT_CHECKED_LEGAL: {
    category: "Manage",
    subCategory: "Payment",
    action: "checked legal",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_REQUEST_CHANGE_PAY_REQUEST: {
    category: "Manage",
    subCategory: "Payment",
    action: "request token",
    detail: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_REQUEST_CHANGE_PAY_ERROR: {
    category: "Manage",
    subCategory: "Payment",
    action: "request token",
    detail: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_REQUEST_CHANGE_PAY_SUCCESS: {
    category: "Manage",
    subCategory: "Payment",
    action: "request token",
    detail: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_SUBMIT_REQUEST: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Submit",
    detail: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_SUBMIT_ERROR: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Submit",
    detail: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_SUBMIT_SUCCESS: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Submit",
    detail: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_CANCEL_REQUEST: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Cancel",
    detail: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_CANCEL_SUCCESS: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Cancel",
    detail: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_BAGS_CHANGE_CANCEL_ERROR: {
    category: "Manage",
    subCategory: "Bags change",
    action: "Cancel",
    detail: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_DISABLED_BAGS_LEARN_MORE_CLICK: {
    category: "Manage",
    subCategory: "Disabled bags",
    action: "learn more click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PASSENGERS_BAGS_SELECTED: {
    category: "Manage",
    subCategory: "Bags change",
    action: "selected",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CANCEL_REQUEST: {
    category: "Manage",
    subCategory: "Cancel",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CANCEL_ERROR: {
    category: "Manage",
    subCategory: "Cancel",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CANCEL_SUCCESS: {
    category: "Manage",
    subCategory: "Cancel",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_CHECK_FLIGHT: {
    category: "Manage",
    subCategory: "Change flights",
    action: "check flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_CHECK_FLIGHT_ERROR: {
    category: "Manage",
    subCategory: "Change flights",
    action: "check flight error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_CHECK_FLIGHT_COMPLETED: {
    category: "Manage",
    subCategory: "Change flights",
    action: "check flight completed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_CLEARED_ALTERNATIVE: {
    category: "Manage",
    subCategory: "Change flights",
    action: "cleared alternative",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_CLEARED_FLIGHT: {
    category: "Manage",
    subCategory: "Change flights",
    action: "cleared flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_MODAL_OPENED: {
    category: "Manage",
    subCategory: "Change flights",
    action: "modal opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_HISTORY_MODAL_OPENED: {
    category: "Manage",
    subCategory: "Change flights history",
    action: "modal opened",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SEARCHING_ALTERNATIVES: {
    category: "Manage",
    subCategory: "Change flights",
    action: "searching alternatives",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SEARCHING_ALTERNATIVES_COMPLETED: {
    category: "Manage",
    subCategory: "Change flights",
    action: "searching alternatives completed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SEARCHING_ALTERNATIVES_FAILED: {
    category: "Manage",
    subCategory: "Change flights",
    action: "searching alternatives failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SELECTED_ALTERNATIVE: {
    category: "Manage",
    subCategory: "Change flights",
    action: "selected alternative",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SELECTED_FLIGHT: {
    category: "Manage",
    subCategory: "Change flights",
    action: "selected flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_SELECTED_FLIGHTS_TO_CHANGE: {
    category: "Manage",
    subCategory: "Change flights",
    action: "selected flights to change",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_FORM_SUBMIT_SUCCESS: {
    category: "Manage",
    subCategory: "Change flights",
    action: "submit alternative form success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_CHANGE_FLIGHTS_FORM_SUBMIT_ERROR: {
    category: "Manage",
    subCategory: "Change flights",
    action: "submit alternative form error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_COMPLETION_SUBMITTED: {
    category: "Manage",
    subCategory: "Travel document fill",
    action: "submitted",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_COMPLETION_SUCCESS: {
    category: "Manage",
    subCategory: "Travel document fill",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_COMPLETION_ERROR: {
    category: "Manage",
    subCategory: "Travel document fill",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_COMPLETION_VALIDATION_ERROR: {
    category: "Manage",
    subCategory: "Travel document fill",
    action: "validation error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_COMPLETION_STATUS: {
    category: "Manage",
    action: "Completion status",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAX_CHANGE_FORM_OPENED: {
    category: "Manage",
    subCategory: "Passenger edit",
    action: "form open",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAX_CHANGE_FORM_SUBMIT: {
    category: "Manage",
    subCategory: "Passenger edit",
    action: "form submit",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAX_CHANGE_OFFER: {
    category: "Manage",
    subCategory: "Passenger edit",
    action: "offer",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_INSURANCE_CHANGE_REQUEST_ERROR: {
    category: "Manage",
    subCategory: "Insurance change",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_INSURANCE_CHANGE_REQUEST_REQUEST: {
    category: "Manage",
    subCategory: "Insurance change",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PENDING_REFUND_REQUEST_ERROR: {
    category: "Manage",
    subCategory: "Refund",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PENDING_REFUND_REQUEST_REQUEST: {
    category: "Manage",
    subCategory: "Refund",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_REQUEST: {
    category: "Manage",
    subCategory: "Pending request",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_SUCCESS: {
    category: "Manage",
    subCategory: "Pending request",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_PENDING_REQUESTS_SUBMIT_ERROR: {
    category: "Manage",
    subCategory: "Pending request",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_ADDITIONAL_SERVICE_REQUEST_REQUEST: {
    category: "Manage",
    subCategory: "Additional service",
    action: "request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_ADDITIONAL_SERVICE_REQUEST_SUCCESS: {
    category: "Manage",
    subCategory: "Additional service",
    action: "success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_ADDITIONAL_SERVICE_REQUEST_ERROR: {
    category: "Manage",
    subCategory: "Additional service",
    action: "error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_SPORTS_EQUIPMENT_PREV_STEP: {
    category: "Manage",
    subCategory: "Sports Equipment",
    action: "previous step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_SPORTS_EQUIPMENT_NEXT_STEP: {
    category: "Manage",
    subCategory: "Sports Equipment",
    action: "next step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_SPORTS_EQUIPMENT_UNAVAILABLE: {
    category: "Manage",
    subCategory: "Sports Equipment",
    action: "Sports Equipment unavailable displayed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_SPECIAL_ASSISTANCE_PREV_STEP: {
    category: "Manage",
    subCategory: "Special Assistance",
    action: "previous step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_SPECIAL_ASSISTANCE_NEXT_STEP: {
    category: "Manage",
    subCategory: "Special Assistance",
    action: "next step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_SPECIAL_ASSISTANCE_UNAVAILABLE: {
    category: "Manage",
    subCategory: "Special Assistance",
    action: "Special Assistance unavailable displayed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_PETS_PREV_STEP: {
    category: "Manage",
    subCategory: "Pets",
    action: "previous step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_PETS_NEXT_STEP: {
    category: "Manage",
    subCategory: "Pets",
    action: "next step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_PETS_UNAVAILABLE: {
    category: "Manage",
    subCategory: "Pets",
    action: "Pets unavailable displayed",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_MUSICAL_EQUIPMENT_PREV_STEP: {
    category: "Manage",
    subCategory: "Musical Equipment",
    action: "previous step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_MUSICAL_EQUIPMENT_NEXT_STEP: {
    category: "Manage",
    subCategory: "Musical Equipment",
    action: "next step clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_MUSICAL_EQUIPMENT_UNAVAILABLE: {
    category: "Manage",
    subCategory: "Musical Equipment",
    action: "Musical Equipment unavailable displayed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_MAPPER_UNSUPPORTED_STATUS: {
    category: "Manage",
    subCategory: "mapperManageBooking",
    action: "Unsupported status",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  // Trustpilot
  MANAGE_THANKYOU_TRUSTPILOT_CARD_CLICK: {
    category: "Manage",
    subCategory: "Thankyou widget",
    action: "Trustpilot card click",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  MANAGE_THANKYOU_TRUSTPILOT_CARD_SHOWN: {
    category: "Manage",
    subCategory: "Thankyou widget",
    action: "Trustpilot card shown",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  MANAGE_WATCHDOG_ACTIVATE_ERROR: {
    category: "Account",
    subCategory: "Watchdog",
    action: "activate error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MANAGE_WATCHDOG_DEACTIVATE_ERROR: {
    category: "Account",
    subCategory: "Watchdog",
    action: "deactivate error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAYMENT_ZOOZ_ADD_PAYMENT_METHOD_REQUEST: {
    category: "Payment",
    action: "ZooZ AddPaymentMethod request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  PAYMENT_ZOOZ_ADD_PAYMENT_METHOD_FAIL: {
    category: "Payment",
    action: "ZooZ AddPaymentMethod fail",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  PAYMENT_ZOOZ_ADD_PAYMENT_METHOD_SUCCESS: {
    category: "Payment",
    action: "ZooZ AddPaymentMethod success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAYMENT_ZOOZ_INITIALIZE_ERROR: {
    category: "Payment",
    subCategory: "Zooz",
    action: "initializing error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAYMENT_POS_TOKENIZE_REQUEST: {
    category: "Payment",
    action: "POS Tokenize request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  PAYMENT_POS_TOKENIZE_FAIL: {
    category: "Payment",
    action: "POS Tokenize fail",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  PAYMENT_POS_TOKENIZE_SUCCESS: {
    category: "Payment",
    action: "POS Tokenize success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true, datadog: true },
  },
  PAYMENT_POS_ADD_PAYMENT_METHOD_SUCCESS: {
    category: "Payment",
    action: "POS AddPaymentMethod success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAYMENT_POS_INITIALIZE_ERROR: {
    category: "Payment",
    subCategory: "POS",
    action: "initializing error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  DEEPLINK_FAILED_TO_DISPLAY: {
    category: "Manage",
    subCategory: "Deeplink",
    action: "Failed to display",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  DEEPLINK_UNKNOWN_VALUE: {
    category: "Manage",
    subCategory: "Deeplink",
    action: "Unknown value",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FLIGHTS_API_ERROR: {
    category: "General",
    action: "FlightsAPI Request error",
    legacyName: "FlightsAPI Request error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FLIGHTS_API_PARSE_ERROR: {
    category: "General",
    action: "FlightsAPI Response parse error",
    legacyName: "FlightsAPI Response parse error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FLIGHTS_API_SUCCESS: {
    category: "General",
    action: "FlightsAPI ok",
    legacyName: "FlightsAPI ok",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FLIGHTS_SEARCH_FORM: {
    category: "Search",
    action: "search form",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  SEARCH_FLIGHTS_RESULTS_TOGGLE: {
    category: "Search",
    action: "flight toggle",
    destinations: { logmole: true, exponea: true, ga: true, bigQuery: true },
  },
  SEARCH_FLIGHTS_RESULTS_TOGGLE_TRACKING_FAILED: {
    category: "Search",
    subCategory: "Flight toggle tracking",
    action: "Tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FLIGHTS_RESULTS_CLICK: {
    category: "Search",
    action: "booking link click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_FORM_RADIUS_CROSS_CLICK: {
    category: "General",
    action: "Radius cross click",
    legacyName: "Radius cross click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_AGGREGATE_RESULT_NOT_IN_PLACES: {
    category: "General",
    action: "Aggregate result not in places probably airport without city",
    legacyName: "Aggregate result not in places probably airport without city",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_FIND_BY_ID_OK: {
    category: "General",
    action: "PlacesAPI - findById ok",
    legacyName: "PlacesAPI - findById ok",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_FIND_BY_ID_REQUEST_ERROR: {
    category: "General",
    action: "PlacesAPI - findById Request error",
    legacyName: "PlacesAPI - findById Request error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_FIND_BY_ID_RESPONSE_PARSE_ERROR: {
    category: "General",
    action: "PlacesAPI - findById Response parse error",
    legacyName: "PlacesAPI - findById Response parse error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_REGISTER_IMPORTANCE_ERROR: {
    category: "General",
    action: "PlacesAPI registerImportance error",
    legacyName: "PlacesAPI registerImportance error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_REQUEST_ERROR: {
    category: "General",
    action: "PlacesAPI Request error",
    legacyName: "PlacesAPI Request error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_PLACES_API_RESPONSE_PARSE_ERROR: {
    category: "General",
    action: "PlacesAPI Response parse error",
    legacyName: "PlacesAPI Response parse error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_RESULTS_STORE_ERROR: {
    category: "General",
    action: "ResultsStore error",
    legacyName: "ResultsStore error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_RESULTS_CHEAPER_OPTION: {
    category: "Search",
    subCategory: "cheaper option tip",
    action: "click",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_TRACK_SEARCH_TRACKING_FAILED: {
    category: "Search",
    subCategory: "trackSearch",
    action: "tracking failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_WATCHDOG_CREATED: {
    category: "Watchdog",
    action: "Created",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_WATCHDOG_ERROR: {
    category: "Watchdog",
    action: "Error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_GO_TO_PARTNER_BOOKING: {
    category: "Search",
    action: "Go to partner s booking",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SEARCH_MAP_NOT_LOADED: {
    category: "Search",
    action: "Map not loaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  REFUND_ERROR: {
    category: "Refund",
    action: "Submit error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  REFUND_SUCCESS: {
    category: "Refund",
    action: "Submit success",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SCHEDULE_CHANGES_VIEW_ORIGINAL: {
    category: "General",
    action: "Schedule Changes View Original Flight",
    legacyName: "Schedule Changes View Original Flight",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SCHEDULE_CHANGES_MAPPER_INCONSISTENT_STATE: {
    category: "General",
    action: "Schedule Changes Inconsistent state received from backend",
    legacyName: "Schedule Changes Inconsistent state received from backend",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  SCHEDULE_CHANGES: {
    category: "General",
    action: "Schedule Changes",
    legacyName: "Schedule Changes",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  API_REQUEST: {
    category: "General",
    action: "API Request",
    legacyName: "API Request",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  API_FAIL: {
    category: "General",
    action: "API Call FAIL",
    legacyName: "API Call FAIL",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  API_SUCCESS: {
    category: "General",
    action: "API Call SUCCESS",
    legacyName: "API Call SUCCESS",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  CONTENT_FEEDBACK_LOADING: {
    category: "Content",
    subCategory: "Feedback",
    action: "Loading",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  CONTENT_FEEDBACK_SUBMIT: {
    category: "Content",
    subCategory: "Feedback",
    action: "Submit",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  CONTENT_FEEDBACK_PREFILL: {
    category: "Content",
    subCategory: "Feedback",
    action: "User data prefilled",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  CONTENT_FEEDBACK_TOPIC_SELECTED: {
    category: "Content",
    subCategory: "Feedback",
    action: "topicSelected",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  CONTENT_PRIVACY_POLICY_OPEN: {
    category: "Content",
    subCategory: "Privacy Policy",
    action: "Open",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  CONTENT_FEEDBACK_API_ERROR: {
    category: "Booking",
    subCategory: "Fare conditions",
    action: "api error",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: false },
  },
  CONTENT_COOKIES_CHANGED: {
    category: "Cookies",
    action: "Cookies policy changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PAYMENT_STATUS_RETURNED_TEXT_TYPE: {
    category: "Booking",
    subCategory: "Payment status",
    action: "Returned text type",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  BOOKING_PULL_PAYMENT_STATUS_OUT_OF_RETRIES: {
    category: "Booking",
    subCategory: "Pull payment status",
    action: "Out of retries",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PULL_PAYMENT_STATUS_RESPONSE: {
    category: "Booking",
    subCategory: "Pull payment status",
    action: "Status response",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true, datadog: true },
  },
  BOOKING_PAID_CHECK_IN_ADDED: {
    category: "Booking",
    subCategory: "Online Check in",
    action: "Added",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_PAID_CHECK_IN_REMOVED: {
    category: "Booking",
    subCategory: "Online Check in",
    action: "Removed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_AIR_HELP_ADDED: {
    category: "Booking",
    subCategory: "AirHelp",
    action: "AirHelp service added",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_AIR_HELP_REMOVED: {
    category: "Booking",
    subCategory: "AirHelp",
    action: "AirHelp service removed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_INSURANCE_MODAL_OPENED: {
    category: "Booking",
    subCategory: "Ancillaries",
    action: "Insurance modal opened",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_INSURANCE_MODAL_CLOSED: {
    category: "Booking",
    subCategory: "Ancillaries",
    action: "Insurance modal closed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_BLUE_RIBBON_BAGS_ADDED: {
    category: "Booking",
    subCategory: "BlueRibbongBags",
    action: "BlueRibbongBags service added",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_BLUE_RIBBON_BAGS_REMOVED: {
    category: "Booking",
    subCategory: "BlueRibbongBags",
    action: "BlueRibbongBags service removed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_FAST_TRACK_FAQ_OPENED: {
    category: "Booking",
    subCategory: "FastTrack",
    action: "FastTrack SmartFAQ link clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_FAST_TRACK_BGY_FAQ_OPENED: {
    category: "Booking",
    subCategory: "FastTrack",
    action: "FastTrack Bergamo learn more clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_FAST_TRACK_NCE_FAQ_OPENED: {
    category: "Booking",
    subCategory: "FastTrack",
    action: "FastTrack Nice learn more clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  ANCILLARY_BLUE_RIBBON_BAGS_FAQ_OPENED: {
    category: "Booking",
    subCategory: "BlueRibbonBags",
    action: "BlueRibbonBags SmartFAQ link clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  STAR_ADD_ITEM: {
    category: "General",
    subCategory: "Starred",
    action: "Add item",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  STAR_REMOVE_ITEM: {
    category: "General",
    subCategory: "Starred",
    action: "Remove item",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  STAR_NAVBAR_CLICK: {
    category: "General",
    subCategory: "Starred",
    action: "Navbar popup shown",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  SHARE_ACTION_CLICK: {
    category: "General",
    subCategory: "Share",
    action: "Click on share button",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SEARCH_BREADCRUMB_CLICKED: {
    category: "Booking",
    subCategory: "Breadcrumb",
    action: "Search breadcrumb clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },

  MMB_DEEPLINK_MODAL: {
    category: "Manage",
    subCategory: "Deeplink",
    action: "Open Modal",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_SHOT_RECEIVED: {
    category: "Manage",
    subCategory: "SHOT",
    action: "response received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MMB_LOAD_BOOKING_STATUS_NULL: {
    category: "Manage",
    subCategory: "Load booking",
    action: "retry api",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: false },
  },
  MMB_UNKNOWN_PENDING_REFUND_REASON: {
    category: "Manage",
    subCategory: "Pending Refund",
    action: "unknown reason received",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_ADDITIONAL_SERVICE_MAPPER_ERROR: {
    category: "Manage",
    subCategory: "Mapper",
    action: "additional service failed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MMB_ADDITIONAL_BAGS_ZERO_PRICE: {
    category: "Manage",
    subCategory: "Mapper",
    action: "additional bags wrong price",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MMB_ADDITIONAL_BAGS_WRONG_ABID: {
    category: "Manage",
    subCategory: "Mapper",
    action: "wrong additional bags abid received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MMB_INVOICE_DOWNLOAD: {
    category: "Manage",
    subCategory: "Invoice",
    action: "invoice downloaded",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_CONFIRMATION_EMAIL_DOWNLOAD: {
    category: "Manage",
    subCategory: "Confirmation Email",
    action: "confirmation email downloaded",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_TICKETING_CONTACT_US_MODAL_OPEN: {
    category: "Manage",
    subCategory: "Ticketing",
    action: "modal opened",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  // MMB CANCELLATIONS
  MMB_CANCELLATION_OPEN: {
    category: "Manage",
    subCategory: "Cancellation",
    action: "opened",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_CANCELLATION_SUBMIT: {
    category: "Manage",
    subCategory: "Cancellation",
    action: "submitted",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_CANCELLATION_CHOOSE_REASON: {
    category: "Manage",
    subCategory: "Cancellation",
    action: "reason chosen",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_CANCELLATION_SUBMIT_REASON: {
    category: "Manage",
    subCategory: "Cancellation",
    action: "reason submitted",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  // MMB REFUNDS
  MMB_REFUNDS_OPEN: {
    category: "Manage",
    subCategory: "Refunds",
    action: "modal opened",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_CHOOSE_REASON: {
    category: "Manage",
    subCategory: "Refunds",
    action: "reason chosen",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_RAF_SUBMIT: {
    category: "Manage",
    subCategory: "Refunds",
    action: "raf submitted",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_GUIDE_SUBMIT: {
    category: "Manage",
    subCategory: "Refunds",
    action: "guide submitted",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_MEDICAL_SUBMIT: {
    category: "Manage",
    subCategory: "Refunds",
    action: "medical submitted",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_CONTINUE: {
    category: "Manage",
    subCategory: "Refunds",
    action: "button continue clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_BACK: {
    category: "Manage",
    subCategory: "Refunds",
    action: "button back clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_PICK_PASSENGER: {
    category: "Manage",
    subCategory: "Refunds",
    action: "passenger picked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_PICK_FLIGHT: {
    category: "Manage",
    subCategory: "Refunds",
    action: "flight picked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_CLOSE: {
    category: "Manage",
    subCategory: "Refunds",
    action: "modal closed",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_SET_CALCULATOR_TYPE: {
    category: "Manage",
    subCategory: "Calculator",
    action: "type selected",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_CALCULATOR_SUBMIT: {
    category: "Manage",
    subCategory: "Calculator",
    action: "submitted",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_REFUNDS_ASSISTED_SUBMIT: {
    category: "Manage",
    subCategory: "Refunds",
    action: "assisted submitted",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },

  // MMB PAYMENT
  MMB_PAYMENT_OPEN: {
    category: "Manage",
    subCategory: "Payment",
    action: "modal opened",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_PAYMENT_CLICK: {
    category: "Manage",
    subCategory: "Payment",
    action: "button pay clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_PAYMENT_DONE: {
    category: "Manage",
    subCategory: "Payment",
    action: "payment done",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_MAPPER_SERVICES_UNKNOWN_CATEGORY: {
    category: "Manage",
    subCategory: "Mapper",
    action: "unknown service category received",
    destinations: { logmole: false, exponea: false, ga: false, bigQuery: true },
  },
  // MMB BAGS
  MMB_BAGGAGE_OPEN: {
    category: "Manage",
    subCategory: "Baggage",
    action: "modal opened",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_BAGGAGE_CLOSE: {
    category: "Manage",
    subCategory: "Baggage",
    action: "modal closed",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_BAGGAGE_ORDERED: {
    category: "Manage",
    subCategory: "Baggage",
    action: "baggage ordered",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_BAGGAGE_NO_ITINERARY_FAQ_OPEN: {
    category: "Manage",
    subCategory: "Baggage",
    action: "SmartFAQ link clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  MMB_TIMELINE_OPEN: {
    category: "Manage",
    subCategory: "Timeline",
    action: "opened",
    destinations: { logmole: false, exponea: false, ga: false, bigQuery: true },
  },
  MMB_TIMELINE_CLOSE: {
    category: "Manage",
    subCategory: "Timeline",
    action: "closed",
    destinations: { logmole: false, exponea: false, ga: false, bigQuery: true },
  },
  MMB_PAXATA_FAQ_OPEN: {
    category: "Manage",
    subCategory: "PAX ATA",
    action: "SmartFAQ link clicked",
    destinations: { logmole: false, exponea: true, ga: false, bigQuery: true },
  },
  // BAGGAGE
  MMB_ADD_BAGS_PASSENGER_CHANGE_HAND_BAGS: {
    category: "Manage",
    subCategory: "Baggage",
    action: "hand bags changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  MMB_ADD_BAGS_PASSENGER_CHANGE_HOLD_BAGS: {
    category: "Manage",
    subCategory: "Baggage",
    action: "hold bags changed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  // BOOKING FARE_TYPES
  BOOKING_CLICK_ON_FARE_TYPES: {
    category: "Booking",
    subCategory: "FareTypes",
    action: "",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  // BOOKING SERVICE_PACKAGES
  BOOKING_CUSTOMER_CARE_TYPE: {
    category: "Booking",
    subCategory: "CustomerCare",
    action: "Customer care package selected",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SERVICEPACKAGE_OPTION_CLICKED: {
    category: "Booking",
    subCategory: "ServicePackage",
    action: "",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  BOOKING_SERVICEPACKAGE_LEARNMORE: {
    category: "Booking",
    subCategory: "ServicePackage",
    action: "LearnMore",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_FAST_TRACK_LEARNMORE: {
    category: "Manage",
    subCategory: "FastTrack",
    action: "LearnMore clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_FAST_TRACK_DOWNLOAD: {
    category: "Manage",
    subCategory: "FastTrack",
    action: "Download QR code clicked",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_ANCILLARIES_ERROR_XHR: {
    category: "Manage",
    subCategory: "Ancillaries",
    action: "Fetch ancillaries XHR failed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MMB_ANCILLARIES_DOCUMENTS_ERROR_XHR: {
    category: "Manage",
    subCategory: "Ancillaries",
    action: "Fetch ancillaries documents XHR failed",
  },
  MMB_SEATING_ANCILLARIES_DATA_INCONSISTENCY: {
    category: "Manage",
    subCategory: "Ancillaries",
    action: "Inconsistent seating ancillaries data received",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  // CHECK-IN
  MANAGE_CHECK_IN_SUBMIT_SUCCESS: {
    category: "Manage",
    subCategory: "Check-in",
    action: "input success",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_CHECK_IN_SECTION_DISPLAYED: {
    category: "Manage",
    subCategory: "Check-in",
    action: "input displayed",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_CHECK_IN_ALL_COMPLETE: {
    category: "Manage",
    subCategory: "Check-in",
    action: "all passengers complete",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  PCI_IFRAME_LOADED: {
    category: "Payment",
    action: "pci iframe loaded",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_FIRST_MESSAGE_RECEIVED: {
    category: "Payment",
    action: "pci iframe first message received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_LOAD_ERROR: {
    category: "Payment",
    action: "pci iframe failed to load",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_TIMEOUT: {
    category: "Payment",
    action: "pci iframe did not load in 10 seconds",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_POST_MESSAGE_FAILED: {
    category: "Payment",
    action: "failed post message to pci iframe",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_IMAGE_LOADED: {
    category: "Payment",
    action: "image from PCI domain loaded successfully",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_IMAGE_NOT_LOADED: {
    category: "Payment",
    action: "failed to load image from pci domain",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_WEB_PAYMENT_AVAILABLE: {
    category: "Payment",
    action: "Web payment available",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_ALTERNATE_TEXT_DISPLAYED: {
    category: "Payment",
    action: "Alternate iframe text displayed",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_MESSAGE_RECEIVED: {
    category: "Payment",
    action: "pci iframe message received",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_SETTING_HEIGHT: {
    category: "Payment",
    action: "pci iframe setting height of iframe element",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_CANNOT_SET_HEIGHT: {
    category: "Payment",
    action: "pci iframe can not set height element not exist or height is zero",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_TRY_PAY_WITHOUT_VALID_DATA: {
    category: "Payment",
    action: "User press paybutton without valid data in PCI iframe",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_PRELOADER_SET_TO_FALLBACK: {
    category: "Payment",
    action: "pci preloader switch to inline-fallback",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PCI_IFRAME_PRELOADER_SUCCESS_PRELOAD: {
    category: "Payment",
    action: "pci preloader successfully preload pci iframe",
    destinations: { logmole: true, exponea: false, ga: false, bigQuery: true },
  },
  PAGE_NOT_FOUND: {
    category: "General",
    action: "Page not found",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  MANAGE_CHECK_IN_INCONSISTENT_DATA: {
    category: "Manage",
    subCategory: "Check-in",
    action: "inconsistent data",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  SELECTED_SEATING_OPTION: {
    category: "Booking",
    subCategory: "Additional service",
    action: "service added",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  SEATING_DATA_RECEIVED: {
    category: "Booking",
    subCategory: "Additional service",
    action: "Check ancillaries API seating loaded",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
  CHECK_ANCILLARIES_ERROR: {
    category: "Booking",
    subCategory: "Additional service",
    action: "Check ancillaries API error",
    destinations: { logmole: true, exponea: true, ga: false, bigQuery: true },
  },
}

type CuckooEventKeys = $Keys<typeof cuckooEventsUnchecked>
export const cuckooEvents: {
  [CuckooEventKeys]: CuckooEvent,
} = cuckooEventsUnchecked

export const apiEndpoints = {
  cuckoo: { host: "main", path: "logstash" },
  checkFlights: { host: "booking", path: "api/v0.1/check_flights?v=2" },
  travelTip: { host: "main", path: "travel_tip" },
  advices: { host: "booking", path: "api/v0.1/travel_info" },
  saveBooking: { host: "booking", path: "api/v0.1/save_booking?v=2" },
  payTracking: { host: "booking", path: "api/v0.1/pay_tracking" },
  confirmPayment: { host: "booking", path: "api/v0.1/confirm_payment" },
  alternatives: { host: "booking", path: "api/v0.1/alternative" },
  fareCondition: { host: "booking", path: "api/v0.1/fare_conditions" },
  nameCorrector: {
    host: "booking",
    path: "https://name-corrector-dev.skypicker.com/corrector",
  },
  preBookingApi: {
    host: "booking",
    path: "https://booking-api.skypicker.com/prepay_pnr",
  },
  storedPassengers: { host: "booking", path: "mmb/v1/stored_passengers" },
  servicePackages: { host: "booking", path: "check_ancillaries" },
}

export const getApiEndpoints = (branding: Map<string, mixed>) => {
  const checkFlightsApi = branding.getIn(["api", "check_flights"])
  const saveBookingApi = branding.getIn(["api", "save_booking"])
  const confirmPaymentApi = branding.getIn(["api", "confirm_payment"])
  const advicesApi = branding.getIn(["api", "travel_info"])

  return {
    ...apiEndpoints,
    checkFlights: checkFlightsApi ? { host: checkFlightsApi, path: "" } : apiEndpoints.checkFlights,
    saveBooking: saveBookingApi ? { host: saveBookingApi, path: "" } : apiEndpoints.saveBooking,
    confirmPayment: confirmPaymentApi
      ? { host: confirmPaymentApi, path: "" }
      : apiEndpoints.confirmPayment,
    advices: advicesApi ? { host: advicesApi, path: "" } : apiEndpoints.advices,
  }
}
